import { ArcadeMachineService } from '@/src/services/ArcadeMachineService'
import { LocalStorageService } from '@/src/services/LocalStorageService'
import { MetamaskService } from '@/src/services/MetamaskService'
import { useLoginUser } from '@/src/views/states/LoginUser'
import { useProcessingContactsContext } from '@/src/views/states/ProcessingContactsContext'
import { AkiverseLocker__factory, ArcadeParts__factory } from '@victgame/akiverse-deposit-withdraw-contracts'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { Props } from './Layout'
import {
  ArcadePartCategory,
  NftState,
  useArcadePartsQuery,
  ArcadePartsQuery,
  useDepositArcadePartMutation,
  useJunksQuery,
} from '../../query.generated'
import { TerasService } from '@/src/services/TerasService'
import { DISABLED_AMS } from '@/src/assets/constants'
import { AKVService } from '@/src/services/AKVService'
import { useMetamaskWalletAddress } from '@/src/views/states/MetamaskWalletAddress'
import { useGameInfo } from '@/src/views/states/Games'
import { getRarityName } from '@/src/services/CraftService'

export const useProps = (): Props => {
  const { games, reload } = useGameInfo()
  const currentMetamskWalletAddress = useMetamaskWalletAddress().walletAddress
  const loginUser = useLoginUser().loginUser
  const userId = loginUser?.id ?? ''
  const walletAddress = loginUser?.walletAddress ?? ''
  const terasBalance = Number(loginUser?.terasBalance ?? 0)
  const akvBalance = Number(loginUser?.akvBalance?.toAKV() ?? 0)
  const [craftState, setCraftState] = useState<Props['craftState']>('Draft')
  const [selectedAmPartsType, setSelectedAmPartsType] = useState<Props['selectedAmPartsType']>('ROM')
  const [selectedRomId, setSelectedRomId] = useState<string | undefined>(
    LocalStorageService.loadCraftPartIds()?.rom,
  )
  const [selectedAccumulatorId, setSelectedAccumulatorId] = useState<string | undefined>(
    LocalStorageService.loadCraftPartIds()?.accumulator,
  )
  const [selectedUpperCabinetId, setSelectedUpperCabinetId] = useState<string | undefined>(
    LocalStorageService.loadCraftPartIds()?.upperCabinet,
  )
  const [selectedLowerCabinetId, setSelectedLowerCabinetId] = useState<string | undefined>(
    LocalStorageService.loadCraftPartIds()?.lowerCabinet,
  )
  const [visibleCraftDialog, setVisibleCraftDialog] = useState(false)
  const { data: arcadePartsQueryData, refetch: arcadePartsQueryRefetch } = useArcadePartsQuery({
    variables: { userId },
  })
  const arcadeParts = arcadePartsQueryData?.arcadeParts ?? []
  const { data: junksQueryData, refetch: junksQueryRefetch } = useJunksQuery({ variables: { userId } })
  const junks =
    junksQueryData?.junks
      .map((d) => ({
        ...d,
        id: d.subCategory,
        metadata: {
          ...d.metadata,
          name: d.metadata.name.replace(/(junk|Junk)/, '').trim(), // UI上Junkじゃないパーツと表示名は同じで追加でジャンクパーツを使っているという旨のラベル付けを行うのでjunk/Junkの表記を消す
        },
      }))
      .sort((a, b) => (a.amount < b.amount ? 1 : -1)) ?? []
  const roms = [
    ...aggregateBySameSubCategory(
      arcadeParts
        .filter((ap) => ap.category === ArcadePartCategory.Rom)
        .filter((ap) => !DISABLED_AMS.includes(ap.subCategory)),
    ),
    ...junks
      .filter((j) => j.category === ArcadePartCategory.Rom)
      .filter((ap) => !DISABLED_AMS.includes(ap.subCategory))
      .map<Props['roms'][number]>((d) => ({
        type: 'Junk',
        id: d.id,
        image: d.metadata.image,
        inAkiverseCounts: d.amount,
        name: d.metadata.name,
        rarity: d.metadata.rarity,
        selectablePartId: d.id,
        subCategory: d.subCategory,
        junksPerPart: d.metadata.junksPerPart,
        disabled: d.metadata.junksPerPart > d.amount,
      })),
  ]
  const accumulators = [
    ...aggregateBySameSubCategory(arcadeParts.filter((ap) => ap.category === ArcadePartCategory.Accumulator)),
    ...junks
      .filter((j) => j.category === ArcadePartCategory.Accumulator)
      .map<Props['accumulators'][number]>((d) => ({
        type: 'Junk',
        id: d.id,
        image: d.metadata.image,
        inAkiverseCounts: d.amount,
        name: d.metadata.name,
        rarity: d.metadata.rarity,
        selectablePartId: d.id,
        subCategory: d.subCategory,
        junksPerPart: d.metadata.junksPerPart,
        disabled: d.metadata.junksPerPart > d.amount,
      })),
  ]
  const upperCabients = [
    ...aggregateBySameSubCategory(
      arcadeParts.filter((ap) => ap.category === ArcadePartCategory.UpperCabinet),
    ),
    ...junks
      .filter((j) => j.category === ArcadePartCategory.UpperCabinet)
      .map<Props['upperCabients'][number]>((d) => ({
        type: 'Junk',
        id: d.id,
        image: d.metadata.image,
        inAkiverseCounts: d.amount,
        name: d.metadata.name,
        rarity: d.metadata.rarity,
        selectablePartId: d.id,
        subCategory: d.subCategory,
        junksPerPart: d.metadata.junksPerPart,
        disabled: d.metadata.junksPerPart > d.amount,
      })),
  ]
  const lowerCabinets = [
    ...aggregateBySameSubCategory(
      arcadeParts.filter((ap) => ap.category === ArcadePartCategory.LowerCabinet),
    ),
    ...junks
      .filter((j) => j.category === ArcadePartCategory.LowerCabinet)
      .map<Props['lowerCabinets'][number]>((d) => ({
        type: 'Junk',
        id: d.id,
        image: d.metadata.image,
        inAkiverseCounts: d.amount,
        name: d.metadata.name,
        rarity: d.metadata.rarity,
        selectablePartId: d.id,
        subCategory: d.subCategory,
        junksPerPart: d.metadata.junksPerPart,
        disabled: d.metadata.junksPerPart > d.amount,
      })),
  ]
  const selectedLowerCabinet = lowerCabinets.find((lc) => lc.id === selectedLowerCabinetId)
  const selectedAccumulator = accumulators.find((ac) => ac.id === selectedAccumulatorId)
  const selectedRom = roms.find((rom) => rom.id === selectedRomId)
  const selectedUpperCabinet = upperCabients.find((uc) => uc.id === selectedUpperCabinetId)
  const game = games.find((e) => e.id === selectedRom?.subCategory)
  const craftRecipe = {
    requiredUC: game?.recipe?.minUpperCabinetGrade ?? 1,
    requiredLC: game?.recipe?.minLowerCabinetGrade ?? 1,
    requiredUCLabel: getRarityName(game?.recipe.minUpperCabinetGrade ?? 1),
    requiredLCLabel: getRarityName(game?.recipe.minLowerCabinetGrade ?? 1),
  }
  const [depositArcadePart] = useDepositArcadePartMutation()
  const { depositingArcadeParts } = useProcessingContactsContext()

  const onClickPrepare = useCallback(async () => {
    if (!currentMetamskWalletAddress) {
      alert('Error : Please Connect to Metamask.')
      return
    }
    if (selectedRom && selectedAccumulator && selectedUpperCabinet && selectedLowerCabinet) {
      LocalStorageService.saveCraftPartIds({
        rom: selectedRom.id,
        accumulator: selectedAccumulator.id,
        upperCabinet: selectedUpperCabinet.id,
        lowerCabinet: selectedLowerCabinet.id,
      })

      setCraftState('Depositing')
      const inWalletParts = [
        selectedRom,
        selectedAccumulator,
        selectedUpperCabinet,
        selectedLowerCabinet,
      ].filter((ap) => ap.type === 'AP' && ap.inAkiverseCounts === 0)
      try {
        // setApprovalForAllしているか確認してしていなかったら実行する
        const isApprovedForAll = await new ArcadeParts__factory()
          .attach(process.env.NEXT_PUBLIC_AP_CONTRACT)
          .connect(MetamaskService.getSigner())
          .isApprovedForAll(walletAddress, process.env.NEXT_PUBLIC_AKIVERSE_LOCKER_ADDRESS)
        if (!isApprovedForAll) {
          await new ArcadeParts__factory()
            .attach(process.env.NEXT_PUBLIC_AP_CONTRACT)
            .connect(MetamaskService.getSigner())
            .setApprovalForAll(process.env.NEXT_PUBLIC_AKIVERSE_LOCKER_ADDRESS, true)
            .then((result) => result.wait())
        }

        const inWalletAPIds = inWalletParts.map((ap) => ap.id)
        await AkiverseLocker__factory.connect(
          process.env.NEXT_PUBLIC_AKIVERSE_LOCKER_ADDRESS,
          MetamaskService.getSigner(),
        )
          .bulkDepositArcadeParts(inWalletAPIds)
          .then(async ({ hash }) => {
            await depositArcadePart({ variables: { input: { ids: inWalletAPIds, hash } } })
          })

        setCraftState('Craftable')
      } catch (e) {
        console.error(e)
        alert('Error')
        setCraftState('Depositable')
      }
    }
  }, [
    selectedRom,
    selectedAccumulator,
    selectedUpperCabinet,
    selectedLowerCabinet,
    depositArcadePart,
    currentMetamskWalletAddress,
    walletAddress,
  ])

  const onClickCraft = useCallback(() => {
    setVisibleCraftDialog(true)
  }, [])

  const onClickAMCraftDialogClose = useCallback(() => {
    setVisibleCraftDialog(false)
  }, [])

  const onCompletedAMCraft = useCallback(() => {
    setVisibleCraftDialog(false)
    arcadePartsQueryRefetch()
    junksQueryRefetch()
    setSelectedAmPartsType('ROM')
    setSelectedRomId(undefined)
    setSelectedAccumulatorId(undefined)
    setSelectedUpperCabinetId(undefined)
    setSelectedLowerCabinetId(undefined)
    LocalStorageService.saveCraftPartIds(undefined)
  }, [arcadePartsQueryRefetch, junksQueryRefetch])

  const creaftedAm: Props['creaftedAm'] =
    selectedRom && selectedAccumulator && selectedUpperCabinet && selectedLowerCabinet
      ? {
          name: ArcadeMachineService.getCraftAmName({ romSubCategory: selectedRom.subCategory, games }),
          animationUrl: ArcadeMachineService.getCraftAmAnimationUrl({
            romSubCategory: selectedRom.subCategory,
          }),
          akvFee: AKVService.getDefaultCraftFee(),
          terasFee: TerasService.getDefaultCraftFee(),
        }
      : undefined

  useLayoutEffect(() => {
    if (
      selectedRom &&
      selectedAccumulator &&
      selectedUpperCabinet &&
      selectedLowerCabinet &&
      [selectedRom.id, selectedAccumulator.id, selectedUpperCabinet.id, selectedLowerCabinet.id].some((id) =>
        depositingArcadeParts.some((depositingAp) => id === depositingAp.id),
      )
    ) {
      setCraftState('WaitingForCompletedDepositing')
    } else if (!selectedRom || !selectedAccumulator || !selectedUpperCabinet || !selectedLowerCabinet) {
      setCraftState('Draft')
    } else {
      const selectedParts = [selectedRom, selectedAccumulator, selectedUpperCabinet, selectedLowerCabinet]
      if (selectedParts.every((ap) => (ap.type === 'AP' ? ap.inAkiverseCounts > 0 : true))) {
        setCraftState('Craftable')
      } else {
        setCraftState('Depositable')
      }
    }
  }, [selectedRom, selectedAccumulator, selectedUpperCabinet, selectedLowerCabinet, depositingArcadeParts])

  useEffect(() => {
    reload()
  }, [reload])

  return {
    roms,
    accumulators,
    upperCabients: upperCabients.map((e) => ({
      ...e,
      disabled:
        !!(craftRecipe.requiredUC > 1 && craftRecipe?.requiredUC !== e.rarity) ||
        (e.type == 'Junk' && e.disabled),
    })),
    lowerCabinets: lowerCabinets.map((e) => ({
      ...e,
      disabled:
        !!(craftRecipe.requiredLC > 1 && craftRecipe?.requiredLC !== e.rarity) ||
        (e.type == 'Junk' && e.disabled),
    })),
    craftState,
    selectedAmPartsType,
    onSelectedAccumulator: setSelectedAccumulatorId,
    onSelectedLowerCabinet: setSelectedLowerCabinetId,
    onSelectedROM: (romId) => {
      setSelectedRomId(romId)

      // 選択済みのパーツがレシピに一致していない場合、クリア
      const newGame = games.find((e) => e.id === romId)
      const newRecipe = {
        requiredUC: newGame?.recipe.minUpperCabinetGrade ?? 1,
        requiredLC: newGame?.recipe.minLowerCabinetGrade ?? 1,
        requiredUCLabel: getRarityName(newGame?.recipe.minUpperCabinetGrade ?? 1),
        requiredLCLabel: getRarityName(newGame?.recipe.minLowerCabinetGrade ?? 1),
      }
      if (!!newRecipe?.requiredUC && newRecipe.requiredUC !== (selectedUpperCabinet?.rarity ?? 1)) {
        setSelectedUpperCabinetId(undefined)
      }
      if (!!newRecipe?.requiredLC && newRecipe.requiredLC !== (selectedLowerCabinet?.rarity ?? 1)) {
        setSelectedLowerCabinetId(undefined)
      }
    },
    onSelectedUpperCabinet: setSelectedUpperCabinetId,
    selectedRom,
    selectedAccumulator,
    selectedLowerCabinet,
    selectedUpperCabinet,
    onSelectPartsType: setSelectedAmPartsType,
    onClickPrepare,
    onClickCraft,
    visibleCraftDialog,
    onClickAMCraftDialogClose,
    onCompletedAMCraft,
    creaftedAm,
    akvBalance,
    terasBalance,
    upperOverlayLabel: craftRecipe && craftRecipe.requiredUC > 1 ? craftRecipe.requiredUCLabel : null,
    lowerOverlayLabel: craftRecipe && craftRecipe.requiredLC > 1 ? craftRecipe.requiredLCLabel : null,
  }
}

const aggregateBySameSubCategory = (parts: ArcadePartsQuery['arcadeParts']) => {
  return Object.values(
    parts.reduce(
      (prev, current) => {
        const subCategory = current.subCategory
        if (!prev[subCategory]) {
          prev[subCategory] = {
            type: 'AP',
            id: current.id,
            subCategory,
            image: current.metadata.image,
            name: current.metadata.name,
            inAkiverseCounts: 0,
            inWalletCounts: 0,
            selectablePartId: '',
            rarity: current.metadata.rarity,
          }
        }
        prev[subCategory]!.inAkiverseCounts += current.state === NftState.InAkiverse ? 1 : 0
        prev[subCategory]!.inWalletCounts += current.state === NftState.InWallet ? 1 : 0

        if (
          !prev[subCategory]!.selectablePartId ||
          parts.find((p) => p.id === prev[subCategory]!.selectablePartId)?.state === NftState.InWallet
        ) {
          prev[subCategory]!.id = current.id
          prev[subCategory]!.selectablePartId = current.id
        }
        return prev
      },
      {} as Record<
        string,
        {
          type: 'AP'
          id: string
          subCategory: string
          image: string
          name: string
          inAkiverseCounts: number
          inWalletCounts: number
          selectablePartId: string
          rarity: number
        }
      >,
    ),
  )
}
